// Rejestracja (artboardy 06a, 06ab, 06b, 06c, 06d).
//
// Adres, hasło DWA RAZY i jedna zgoda. Pole „Imię / nazwa" odpadło: konto zakłada się
// adresem, a nazwę artysty ustawia się w Ustawieniach, gdy jest po co (zasada 8).
// „Powtórz hasło" (decyzja właściciela, 21.09): literówka w jedynym polu hasła zamykała
// człowieka poza kontem, zanim je zobaczył; to samo pole i te same słowa co w resecie hasła.
//
// ZGODA JEST DOMYŚLNIE NIEZAZNACZONA, a „Załóż konto" nieaktywne do czasu jej
// zaznaczenia (decyzja właściciela, R2 rozstrzygnięcie 11). Nazwy dokumentów są
// linkami W ZDANIU zgody, a stuknięcie poza linkiem przełącza pole — cała reszta
// wiersza 44 px jest celem.
//
// Teksty zgody i linki prawne to STREFA CHRONIONA: forma może się zmienić, sens nie.

import { useEffect, useId, useState } from 'react'
import { api } from '../lib/api'
import { Link } from 'react-router'
import { useAuth } from '../auth/AuthContext'
import { useI18n, useT } from '../lib/i18n'
import { usePublicPath } from '../lib/publicPaths'
import AuthShell from '../components/AuthShell'
import Icon from '../components/Icon'
import { Button, Checkbox, Field, PasswordMeter } from '../ds'

/** Ta sama reguła, której pilnuje backend: 8 znaków, cyfra, wielka litera. */
const STRONG = /^(?=.*[A-Z])(?=.*\d).{8,}$/
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function Register() {
  const { register } = useAuth()
  const t = useT()
  const { locale } = useI18n()
  // R8 zdjęło ze ZDANIA na ekranie regułę hasła, ale miernik, który ją pokazuje oku,
  // jest `aria-hidden` — więc po tamtej rundzie formularz nie mówił reguły NIKOMU, kto
  // czyta ekran czytnikiem. Ten sam wzór, co w resecie hasła (`ResetPassword.tsx`):
  // tekst `sr-only` pod polem, podpięty `aria-describedby`, ten sam klucz i te same
  // słowa co tekst błędu. Oba pola hasła, bo to ta sama reguła (N5-R8-REGISTER-RULE-SR).
  const ruleId = useId()
  const pub = usePublicPath()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [acceptTerms, setAcceptTerms] = useState(false)
  // Błędy POD POLAMI, nie jedno zbiorcze zdanie nad formularzem (MAPA 37, nowe teksty):
  // „Sprawdź dane: min. 8 znaków, cyfra…" nie mówi, KTÓRE pole poprawić.
  const [errors, setErrors] = useState<{ email?: boolean; password?: boolean; confirm?: boolean; terms?: boolean }>({})
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)
  // „Wyślij link jeszcze raz": jedno żądanie, potem 60 s odliczania na przycisku.
  const [resend, setResend] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle')
  const [cooldown, setCooldown] = useState(0)
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  async function resendLink() {
    setResend('busy')
    try {
      await api('/auth/resend-verification', { method: 'POST', body: JSON.stringify({ email }) })
      setResend('sent')
      setCooldown(60)
    } catch {
      setResend('failed')  // awaria transportu albo limiter — nigdy ogłaszana jako „wysłane"
    }
  }

  // Błąd pola pokazuje się, gdy człowiek SKOŃCZYŁ je wypełniać, nie przy pierwszej
  // literze: „Podaj poprawny adres e-mail." przy „a" jest prawdą bez wartości. Przycisk
  // zostaje nieaktywny do zaznaczenia zgody (decyzja właściciela), więc walidacja pól
  // musi mieć własny moment — inaczej stan „błędy pól" byłby nieosiągalny.
  const checkEmail = () => setErrors((x) => ({ ...x, email: email.trim().length > 0 && !EMAIL_SHAPE.test(email.trim()) }))
  // Poprawione pierwsze hasło ocenia też drugie pole, gdy to już jest wypełnione: inaczej
  // „Hasła się różnią." wisiałoby pod polem, które właśnie przestało się różnić, albo odwrotnie.
  const checkPassword = () => setErrors((x) => ({
    ...x,
    password: password.length > 0 && !STRONG.test(password),
    confirm: confirm.length > 0 && confirm !== password,
  }))
  const checkConfirm = () => setErrors((x) => ({ ...x, confirm: confirm.length > 0 && confirm !== password }))

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const next = {
      email: !EMAIL_SHAPE.test(email.trim()),
      password: !STRONG.test(password),
      confirm: confirm !== password,
      terms: !acceptTerms,
    }
    setErrors(next)
    if (next.email || next.password || next.confirm || next.terms) {
      document.getElementById(next.email ? 'reg-email' : next.password ? 'reg-pass' : next.confirm ? 'reg-pass2' : 'reg-terms')?.focus()
      return
    }
    setError(null)
    setBusy(true)
    try {
      // Język, w którym stoi ten ekran, zostaje językiem konta: maile i UI po logowaniu (PROD1 FR-3).
      await register(email, password, acceptTerms, '', locale)
      setDone(true)
    } catch {
      setError(t('auth.register.error'))
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    // 06c / 06d: ten sam ekran, tylko przycisk ponownej wysyłki potrafi odliczać.
    return (
      <AuthShell>
        <span className="mp-auth-glyph" aria-hidden="true"><Icon name="Mail" size={48} /></span>
        <h1>{t('auth.register.check.heading')}</h1>
        <p className="mp-auth-body" data-testid="register-check-email">
          {t('auth.register.check.sentBefore')}<b>{email}</b>{t('auth.register.check.sentAfter')}
        </p>
        <div className="mp-auth-row">
          <button type="button" className="mn-lnk" data-testid="register-resend"
                  disabled={resend === 'busy' || cooldown > 0} onClick={() => void resendLink()}>
            {cooldown > 0 ? t('auth.register.check.resendWait', { s: cooldown }) : t('auth.register.check.resend')}
          </button>
        </div>
        {resend === 'failed' && <p className="mp-err" role="alert">{t('auth.register.check.resendFailed')}</p>}
        <p className="mp-auth-foot">
          {t('auth.register.check.wrong')}{' '}
          <button type="button" className="mn-lnk"
                  onClick={() => { setDone(false); setResend('idle'); setCooldown(0) }}>
            {t('auth.register.check.again')}
          </button>
        </p>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <h1>{t('auth.register.heading')}</h1>

      {error && <p className="mp-err" role="alert">{error}</p>}

      <form onSubmit={onSubmit} noValidate>
        <Field
          id="reg-email"
          type="email"
          label={t('auth.register.emailLabel')}
          placeholder={t('auth.register.emailPlaceholder')}
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => { setEmail(e.target.value); setErrors((x) => ({ ...x, email: false })) }}
          onBlur={checkEmail}
          error={errors.email ? t('auth.register.err.email') : undefined}
        />
        {/* Bez zastępczego tekstu: JEDNA reguła stoi pod polem, nie w nim. */}
        <Field
          id="reg-pass"
          type="password"
          label={t('auth.register.passwordLabel')}
          autoComplete="new-password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setErrors((x) => ({ ...x, password: false })) }}
          onBlur={checkPassword}
          /* R8: proaktywna podpowiedź znika, bo regułę pokazuje miernik. Ten sam
             klucz ZOSTAJE jako tekst błędu — to jedyny tekst błędu tego pola. */
          error={errors.password ? t('auth.reset.hint') : undefined}
          aria-describedby={ruleId}
        />
        <PasswordMeter value={password} />
        <span className="sr-only" id={ruleId}>{t('auth.reset.hint')}</span>
        {/* Te same słowa co w resecie hasła: jedna rzecz, jedna nazwa. */}
        <Field
          id="reg-pass2"
          type="password"
          label={t('auth.reset.confirmLabel')}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => { setConfirm(e.target.value); setErrors((x) => ({ ...x, confirm: false })) }}
          onBlur={checkConfirm}
          error={errors.confirm ? t('auth.reset.mismatch') : undefined}
          aria-describedby={ruleId}
        />

        <Checkbox
          id="reg-terms"
          className="mp-consent"
          checked={acceptTerms}
          onChange={(e) => { setAcceptTerms(e.target.checked); setErrors((x) => ({ ...x, terms: !e.target.checked })) }}
          error={errors.terms ? t('auth.register.err.terms') : undefined}
          label={
            <>
              {t('auth.register.acceptPrefix')}{' '}
              <Link to={pub('/regulamin')} target="_blank" rel="noreferrer noopener">{t('auth.register.terms')}</Link>
              {' '}{t('auth.register.acceptAnd')}{' '}
              <Link to={pub('/polityka')} target="_blank" rel="noreferrer noopener">{t('auth.register.privacy')}</Link>
            </>
          }
        />

        {/* Nieaktywny do kompletu wymaganych zgód (decyzja właściciela). */}
        <Button type="submit" variant="primary" busy={busy} disabled={!acceptTerms} data-cta="rejestracja">
          {t('auth.register.submit')}
        </Button>

        {/* Warstwa pewności POD przyciskiem (R3, artboardy 06a/06ab/06b): ostatnia
            rzecz przed kliknięciem to zdanie o karcie i nazwa dokumentu, pod którym
            człowiek się podpisuje. To NIE jest zgoda — zgoda stoi wyżej i jest strefą
            chronioną; ten wiersz niczego nie zaznacza i niczego nie wymaga. */}
        <p className="mp-auth-assure">
          {t('auth.register.noCard')}{' '}
          <Link to={pub('/polityka')} target="_blank" rel="noreferrer noopener">{t('landing.footer.privacyFull')}</Link>
        </p>
      </form>

      <p className="mp-auth-foot is-centered">
        {t('auth.register.haveAccount')}{' '}
        <Link to="/login" className="mn-lnk">{t('auth.register.signIn')}</Link>
      </p>
    </AuthShell>
  )
}
